/**
 * Decides whether the assistant is offered to the signed-in account.
 *
 * Three flags rather than one, because "is the assistant ready at all" and "is
 * it ready for *this* kind of account" are different questions and get answered
 * at different times. The assistant's skills are audience-specific — suppliers
 * can edit locations, admins get platform-wide figures, buyers currently get the
 * least — so each audience becomes trustworthy on its own schedule.
 *
 * The shape is a master switch plus two opt-ins:
 *
 * - `assistantChat` off → nobody sees it, whatever the other two say. This is
 *   the kill switch, and it has to be the kind that cannot be undermined by
 *   another flag being left on.
 * - `assistantChat` on → SecurSpace staff see it. Staff are us; they are the
 *   audience we can expose a half-finished skill to without it being a customer
 *   problem. That includes staff **impersonating** a customer via "log in as
 *   account": `loginAsAccount` retains the staff role, so the human at the
 *   keyboard is still an admin, and gating them as a customer would mean the
 *   flags keeping this away from customers also keep it from the people testing
 *   it. Their data stays scoped to the impersonated account either way.
 * - Suppliers and buyers additionally require their own flag, so either
 *   customer-facing audience can be enabled, or pulled, without touching the
 *   other or taking the whole feature down.
 *
 * Note the asymmetry: admins are gated by one flag and customers by two. That is
 * deliberate. The expensive mistake is a customer seeing something unfinished,
 * not an admin missing out for an extra minute.
 */

import type { AssistantCapabilities } from './assistantCapabilities';

export interface AssistantAccessFlags {
  assistantChat: boolean;
  assistantChatSuppliers: boolean;
  assistantChatBuyers: boolean;
}

/**
 * Whether this account may use the assistant.
 *
 * Takes the whole capabilities object rather than just the audience because
 * "which skills apply" and "which human is sitting here" diverge during
 * impersonation, and only the second should decide who sees a prototype.
 *
 * Written as an exhaustive switch on the audience rather than a chain of
 * conditions so that adding a fourth audience is a compile error here instead of
 * silently inheriting whichever branch happened to be last.
 */
export const assistantAvailableFor = (
  capabilities: Pick<AssistantCapabilities, 'audience' | 'isStaff'>,
  flags: AssistantAccessFlags,
): boolean => {
  if (!flags.assistantChat) return false;

  // Checked before the audience switch: staff impersonating a supplier have a
  // 'supplier' audience, and gating them behind the customer flag would hide
  // the assistant from the people meant to be testing it.
  if (capabilities.isStaff) return true;

  switch (capabilities.audience) {
    case 'admin':
      return true;
    case 'supplier':
      return flags.assistantChatSuppliers;
    case 'buyer':
      return flags.assistantChatBuyers;
    default:
      // Unreachable while AssistantAudience has three members. Denying rather
      // than allowing means a future audience has to be granted access on
      // purpose, instead of acquiring it by being added to a union type.
      return false;
  }
};
